import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import { clientsApi, extractOne } from '../../api/facades';

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

function fromRow(r: any) {
  const people = r?.people;
  return {
    name: r?.name ?? '',
    company_name: r?.company_name ?? '',
    client_type: (r?.client_type === 'multiple' || r?.client_type === 'single' ? r.client_type : 'multiple') as 'single' | 'multiple',
    first_name: people?.first_name ?? r?.first_name ?? '',
    last_name: people?.last_name ?? r?.last_name ?? '',
    email: people?.email ?? r?.email ?? '',
    main_contact_position: r?.main_contact_position ?? '',
    mobile: people?.mobile ?? r?.mobile ?? r?.phone ?? '',
    company_address: r?.company_address ?? '',
    address: people?.address ?? r?.address ?? '',
    is_active: (r?.is_active === 'No' ? 'No' : 'Yes') as 'Yes' | 'No',
  };
}

const ClientEditPage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const numId = clientId ? parseInt(clientId, 10) : NaN;

  const [name, setName] = useState('');
  const [company_name, setCompanyName] = useState('');
  const [client_type, setClientType] = useState<'single' | 'multiple'>('multiple');
  const [first_name, setFirstName] = useState('');
  const [last_name, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [main_contact_position, setMainContactPosition] = useState('');
  const [mobile, setMobile] = useState('');
  const [company_address, setCompanyAddress] = useState('');
  const [address, setAddress] = useState('');
  const [is_active, setIsActive] = useState<'Yes' | 'No'>('Yes');

  const { data: res, isLoading, isError } = useQuery({
    queryKey: ['clients', numId],
    queryFn: () => clientsApi.get(numId),
    enabled: !Number.isNaN(numId),
  });
  const row = extractOne(res);

  useEffect(() => {
    if (!row || typeof row !== 'object') return;
    const initial = fromRow(row as any);
    setName(initial.name);
    setCompanyName(initial.company_name);
    setClientType(initial.client_type);
    setFirstName(initial.first_name);
    setLastName(initial.last_name);
    setEmail(initial.email);
    setMainContactPosition(initial.main_contact_position);
    setMobile(initial.mobile);
    setCompanyAddress(initial.company_address);
    setAddress(initial.address);
    setIsActive(initial.is_active);
  }, [row]);

  const update = useMutation({
    mutationFn: () =>
      clientsApi.update(numId, {
        name: name.trim() || undefined,
        company_name: company_name.trim() || null,
        client_type,
        first_name: first_name.trim() || undefined,
        last_name: last_name.trim() || null,
        email: email.trim() || null,
        mobile: mobile.trim() || null,
        address: address.trim() || company_address.trim() || null,
        main_contact_position: main_contact_position.trim() || null,
        company_address: company_address.trim() || null,
        is_active,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client updated.');
      navigate(`/dashboard/clients/${numId}`);
    },
    onError: (e: any) => toast.error(e?.message || 'Update failed.'),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    update.mutate();
  };

  if (!clientId || Number.isNaN(numId) || isError || (!isLoading && !row)) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <h1 className="text-2xl font-bold text-slate-700 dark:text-slate-200 mb-2">Client not found</h1>
        <Button variant="secondary" onClick={() => navigate('/dashboard/clients')}>Back to Clients</Button>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Update client details and main contact.</p>

      <form onSubmit={onSubmit} className="space-y-8">
        <section>
          {/* <h2 className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-3">Company / Name</h2> */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <FormField label="Company name">
                <input className={inputClass} value={company_name} onChange={(e) => setCompanyName(e.target.value)} placeholder="e.g. Chikale Foods Company Limited" />
              </FormField>
            </div>
            <FormField label="Display name">
              <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Display name" />
            </FormField>
            <FormField label="Client type">
              <select className={inputClass} value={client_type} onChange={(e) => setClientType(e.target.value as 'single' | 'multiple')}>
                <option value="single">Individual</option>
                <option value="multiple">Corporate client</option>
              </select>
            </FormField>
          </div>
        </section>

        <section>
          <h2 className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-3">Main contact</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="First name">
              <input className={inputClass} value={first_name} onChange={(e) => setFirstName(e.target.value)} placeholder="Optional" />
            </FormField>
            <FormField label="Last name">
              <input className={inputClass} value={last_name} onChange={(e) => setLastName(e.target.value)} placeholder="Optional" />
            </FormField>
            <FormField label="Email">
              <input className={inputClass} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="contact@example.com" />
            </FormField>
            <FormField label="Position">
              <input className={inputClass} value={main_contact_position} onChange={(e) => setMainContactPosition(e.target.value)} placeholder="e.g. Head of Legal" />
            </FormField>
            <div className="md:col-span-2">
              <FormField label="Mobile">
                <input className={inputClass} value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+265 999 123 456" />
              </FormField>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-3">Company address</h2>
          <FormField label="Address">
            <textarea className={inputClass + ' min-h-[80px]'} value={company_address} onChange={(e) => setCompanyAddress(e.target.value)} placeholder="Full company address" rows={3} />
          </FormField>
          <FormField label="Address (contact)">
            <input className={inputClass} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Optional" />
          </FormField>
        </section>

        <section>
          <FormField label="Active">
            <select className={inputClass} value={is_active} onChange={(e) => setIsActive(e.target.value as 'Yes' | 'No')}>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </FormField>
        </section>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={() => navigate(-1)}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={update.isPending}>{update.isPending ? 'Saving…' : 'Save'}</Button>
        </div>
      </form>
    </div>
  );
};

export default ClientEditPage;
